import React, { useEffect, useState } from "react";
import { ActionIcon } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { getAuthHeaders } from "../../api/authHelper";
import {
    Badge,
    Button,
    Center,
    Group,
    Loader,
    Modal,
    Paper,
    Stack,
    Text,
} from "@mantine/core";

export type ShiftParticipant = {
    personId: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    status: string;
    createdAt?: string;
    checkInTime?: string | null;
};

export type ShiftParticipantsResponse = {
    shiftId: number;
    maxParticipants: number;
    counts: { confirmed: number; waitlist: number; cancelled: number };
    confirmed: ShiftParticipant[];
    waitlist: ShiftParticipant[];
    cancelled: ShiftParticipant[];
};

export default function ParticipantsModal({
    opened,
    onClose,
    title,
    loading: externalLoading,
    data: externalData,
    onRefresh,
}: {
    opened: boolean;
    onClose: () => void;
    title: string;
    loading: boolean;
    data: ShiftParticipantsResponse | null;
    onRefresh?: () => Promise<void> | void;
}) {
    const apiUrl = process.env.REACT_APP_API_URL as string;
    const [movingPersonId, setMovingPersonId] = useState<number | null>(null);

    const [data, setData] = useState<ShiftParticipantsResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const effectiveData = externalData ?? data;
    const effectiveLoading = externalData ? externalLoading : loading;

    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const isFull =
        !!effectiveData &&
        effectiveData.maxParticipants > 0 &&
        effectiveData.counts.confirmed >= effectiveData.maxParticipants;

    const isCheckedIn = (p: ShiftParticipant) => !!p.checkInTime;

    useEffect(() => {
        if (!opened || externalData) return;

        const shiftIdMatch = title?.match(/\d+/);
        if (!shiftIdMatch) return;

        const shiftId = shiftIdMatch[0];

        const load = async () => {
            try {
                setLoading(true);

                const res = await fetch(
                    `${apiUrl}/api/shifts/${shiftId}/participants`
                );

                const json = await res.json();
                setData(json);
            } catch (err) {
                console.error(err);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [opened, externalData, apiUrl, title]);

    const handleMoveToConfirmed = async (personId: number) => {
        if (!effectiveData?.shiftId) return;

        try {
            setMovingPersonId(personId);

            const res = await fetch(
                `${apiUrl}/api/shifts/${effectiveData.shiftId}/register`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        personId,
                        status: "Tilmeldt",
                    }),
                }
            );

            if (!res.ok) throw new Error("Move failed");

            if (onRefresh) await onRefresh();
        } catch (err) {
            console.error(err);
            alert("Kunne ikke flytte personen til tilmeldt.");
        } finally {
            setMovingPersonId(null);
        }
    };

    const formatTime = (iso?: string | null) => {
        if (!iso) return null;

        return new Date(iso).toLocaleString("da-DK", {
            dateStyle: "short",
            timeStyle: "short",
        });
    };

    const handleRemoveFromShift = async (personId: number) => {
        if (!effectiveData?.shiftId) return;

        try {
            setMovingPersonId(personId);

            const res = await fetch(
                `${apiUrl}/api/shifts/${effectiveData.shiftId}/unregister/${personId}`,
                {
                    method: "DELETE",
                    headers: getAuthHeaders(),
                }
            );

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Remove failed");
            }

            if (onRefresh) await onRefresh();
        } catch (err) {
            console.error(err);
            alert("Kunne ikke fjerne personen fra shiftet.");
        } finally {
            setMovingPersonId(null);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={title} size="lg">
            {effectiveLoading ? (
                <Center>
                    <Loader />
                </Center>
            ) : !effectiveData ? (
                <Text c="dimmed">Ingen data.</Text>
            ) : (
                <Stack gap="md">
                    <Group>
                        <Badge variant="light">
                            Tilmeldt: {effectiveData.counts.confirmed}
                        </Badge>
                        <Badge variant="light">
                            Venteliste: {effectiveData.counts.waitlist}
                        </Badge>
                        <Badge variant="light">
                            Frameldt: {effectiveData.counts.cancelled}
                        </Badge>
                        <Badge color="gray" variant="light">
                            Max: {effectiveData.maxParticipants}
                        </Badge>
                    </Group>

                    <Stack gap="xs">
                        <Text fw={700}>Tilmeldte</Text>

                        {effectiveData.confirmed.map((p) => {

                            return (
                                <Paper key={`${p.personId}-c`} withBorder p="sm" radius="md">
                                    <Group justify="space-between">
                                        <div>
                                            <Text fw={600}>
                                                {p.firstName} {p.lastName}
                                            </Text>
                                            <Text size="sm" c="dimmed">
                                                {p.email || ""}
                                                {p.email && p.phone ? " · " : ""}
                                                {p.phone || ""}
                                            </Text>
                                        </div>

                                        <Group gap="xs">
                                            <Badge
                                                size="md"
                                                color={isCheckedIn(p) ? "green" : "red"}
                                                title={isCheckedIn(p) ? "Checked in" : "Not checked in"}
                                            />

                                            {p.checkInTime && (
                                                <Text size="xs" c="dimmed">
                                                    In: {formatTime(p.checkInTime)}
                                                </Text>
                                            )}
                                        </Group>
                                        <Badge color="green" variant="light">Tilmeldt</Badge>
                                        <ActionIcon
                                            color="red"
                                            variant="light"
                                            onClick={() => setConfirmDeleteId(p.personId)}
                                            disabled={movingPersonId === p.personId}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Group>
                                </Paper>
                            );
                        })}
                    </Stack>

                    <Stack gap="xs">
                        <Text fw={700}>Venteliste</Text>

                        {effectiveData.waitlist.map((p) => (
                            <Paper key={`${p.personId}-w`} withBorder p="sm" radius="md">
                                <Group justify="space-between" align="center">
                                    <div>
                                        <Text fw={600}>
                                            {p.firstName} {p.lastName}
                                        </Text>
                                    </div>

                                    <Group gap="sm">
                                        <Badge color="yellow" variant="light">
                                            Venteliste
                                        </Badge>

                                        <Button
                                            size="xs"
                                            disabled={isFull}
                                            onClick={() =>
                                                handleMoveToConfirmed(p.personId)
                                            }
                                            loading={movingPersonId === p.personId}
                                        >
                                            Flyt til tilmeldt
                                        </Button>
                                    </Group>
                                </Group>
                            </Paper>
                        ))}
                    </Stack>
                </Stack>
            )}
            <Modal
                opened={confirmDeleteId !== null}
                onClose={() => setConfirmDeleteId(null)}
                title="Bekræft sletning"
                centered
            >
                <Text size="sm">
                    Are you sure you want to remove this person from the shift?
                </Text>

                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setConfirmDeleteId(null)}>
                        Cancel
                    </Button>

                    <Button
                        color="red"
                        onClick={() => {
                            if (confirmDeleteId !== null) {
                                handleRemoveFromShift(confirmDeleteId);
                                setConfirmDeleteId(null);
                            }
                        }}
                    >
                        Delete
                    </Button>
                </Group>
            </Modal>
        </Modal>
    );
}